import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import {
	formatCurrentEmploymentDateRange,
	sortEmploymentsMostRecentFirst,
} from "@/components/IdentityCard/employmentHistory";

import { classNames } from "@/utils/classNames";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	closeContactLeadPanel,
	contactSelector,
} from "@/pages/Private/redux/contact/contact.slice";
import { DeepResearchPanel } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/DeepResearchPanel";
import { SignalTimeline } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline";
import { CompanyHeader } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/CompanyHeader";
import { ListPersonHeader } from "@/pages/Private/pages/List/pages/Unassigned/components/ListPersonHeader";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useCondenseOnScroll } from "@/utils/useCondenseOnScroll";
import { People, Organization } from "@/pages/Private/pages/Apollo/schema/get-people";

import { Loader } from "@/components/Loader/Loader";

import {
	useGetOrganizationProfilesQuery,
	useGetSingleListPersonQuery,
} from "@/pages/Private/redux/list/list.api";
import { LeadChatTab } from "@/pages/Private/pages/List/components/CampaignOverview/components/LeadChatTab";
import { ListProfilesTab } from "@/pages/Private/pages/List/pages/Unassigned/components/ListProfilesTab";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import { ContactLeadOverviewTab } from "./ContactLeadOverviewTab";
import { ContactLeadLocation } from "./ContactLeadLocation";
import { RecentSignalsBox } from "./RecentSignalsBox";
import { LEAD_PANEL_STRIP_HEIGHT, LeadPanelCondensedHeader } from "./LeadPanelCondensedHeader";
import { LeadContextTabs, LeadContextView } from "./LeadContextTabs";
import { useChatViewEntry } from "./useChatViewEntry";
import { useRetargetLeadPanel } from "./useRetargetLeadPanel";
import { LeadPanelActions } from "./LeadPanelActions/LeadPanelActions";
import { LeadPanelTabs } from "./LeadPanelTabs";
import { useContactLeadPanelSection } from "./useContactLeadPanelSection";
import {
	buildContactLeadPanelTabs,
	ContactLeadPanelTab,
	resolveCurrentCompanyUrl,
} from "./contactLeadPanel.utils";
import { resolveChatListPersonId } from "../utils/resolveChatListPersonId";

export const ContactLeadPanel: FunctionComponent<{
	isZHigher?: boolean;
	/**
	 * Where to portal the panel. Defaults to `document.body` (web app). The
	 * Chrome extension passes its shadow-root container so the panel renders
	 * inside the isolated style scope instead of the host page.
	 */
	portalContainer?: HTMLElement | null;
	/**
	 * Close the panel on a click outside of it. Defaults to `true`. The Chrome
	 * extension disables this because shadow-DOM event retargeting makes the
	 * document-level outside-click check unreliable; it relies on the explicit
	 * close button instead.
	 */
	closeOnOutsideClick?: boolean;
	/**
	 * Push the panel down by this many pixels from the top (and shrink its
	 * height to match). Defaults to `0`. The Chrome extension uses it to make
	 * room for its own header bar above the panel.
	 */
	topOffsetPx?: number;
	/**
	 * Render the collapse/expand grabber. Defaults to `true`.
	 */
	showCloseButton?: boolean;
	/**
	 * Called when the collapse grabber is clicked. Defaults to closing the panel
	 * (Contacts / Global Search). The Chrome extension passes its own hide handler
	 * so the grabber minimizes the panel to its edge launcher instead of closing.
	 */
	onCollapse?: () => void;
	/**
	 * Web app origin. When set (Chrome extension), the "Where this lead is" links
	 * open the destination in the web app in a new tab instead of using the
	 * in-memory router, which has no matching routes in the extension.
	 */
	webAppUrl?: string;
	/**
	 * Opt-in id of the `ListPerson` this prospect maps to. When provided, a "Chat"
	 * tab is shown that drives the per-lead outreach drafting/sending experience.
	 * The Chrome extension and the global "Search Anything" modal pass it (derived
	 * from the resolved prospect's most-recent list membership); the Contacts page
	 * omits it, so the Chat tab never appears there.
	 */
	chatListPersonId?: number | null;
	/**
	 * When `true`, the host will resolve a {@link chatListPersonId} for this lead once its detail
	 * loads (a Chat-enabled surface such as the global "Search Anything" modal). Until the Chat tab
	 * can be shown, the panel renders a loading spinner instead of the body — so the Lead Panel | Chat
	 * tabs appear together rather than the toggle popping in after the body. Defaults to `false`.
	 */
	chatPending?: boolean;
	/**
	 * Which context view a freshly opened / re-targeted lead starts on. The Feed's
	 * review-now and "Write manually" flows pass `"chat"` so the panel opens on the
	 * drafts (PREC-3635 P6 D6); everything else omits it — the default keeps the
	 * existing lead-panel-first behavior (Contacts, Global Search, extension).
	 */
	initialContextView?: LeadContextView;
	/**
	 * Optional overflow/action menu rendered in the top-right corner of the
	 * identity "people box". Only the Chrome extension passes it (its lead-panel
	 * overflow menu: re-enrich / unsubscribe / open in Signalist); web Contacts
	 * and Global Search omit it, so nothing renders there.
	 */
	headerMenu?: ReactNode;
	/**
	 * Extension override for the company card's "Find more leads at {company}" button. When set, the
	 * button delegates here (the extension opens its native in-panel find-people view) instead of the
	 * web {@link GlobalSearchModal}. Web hosts omit it, keeping the default modal behavior.
	 */
	onFindMoreLeads?: (company: { name?: string; domain?: string }) => void;
	/**
	 * Which content tab a freshly opened / re-targeted lead starts on. Defaults to
	 * `"overview"`. The onboarding tour's Deep Research step passes `"research"` so
	 * the panel opens straight on Deep Research / Executive Summary (PREC-3745).
	 */
	initialTab?: ContactLeadPanelTab;
	onSectionOpen?: (section: ContactLeadPanelTab | "chat") => void;
}> = ({
	isZHigher = false,
	portalContainer,
	closeOnOutsideClick = true,
	topOffsetPx = 0,
	showCloseButton = true,
	webAppUrl,
	chatListPersonId,
	chatPending = false,
	initialContextView = "lead-panel",
	headerMenu,
	onCollapse,
	onFindMoreLeads,
	initialTab = "overview",
	onSectionOpen,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	// The panel `<aside>` element, captured via a ref callback (`setPanelEl`). It is both the
	// outside-click boundary below and the portal target handed to the Chat tab, so the chat's
	// dropdowns portal *inside* the panel instead of `document.body` (PREC-3627).
	const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
	// `ListPerson` created by the action row's Compose-list reach-out for a lead that was on no list
	// (PREC-3825). Takes precedence over the host-supplied / detail-derived id, which predates it.
	const [composedListPersonId, setComposedListPersonId] = useState<number | null>(null);
	const { handleCopyClick, renderSnackbar } = useCopyToClipboard();
	// Clicking a colleague on the Profiles tab points this same panel at them.
	const retargetToProfile = useRetargetLeadPanel();
	const {
		isLeadPanelOpen,
		leadPanelProspectPreview,
		leadPanelDetail,
		isLeadPanelDetailLoading,
		leadPanelDetailError,
	} = useAppSelector(contactSelector);
	const leadPanelProspect = leadPanelDetail ?? leadPanelProspectPreview;
	// Top-level Lead Panel | Chat toggle (v81 mockup `lead-ctx-tabs`).
	const {
		view: leadCtxView,
		autoOpenGenerateBar,
		openChatView,
		changeView: setLeadCtxView,
		resetView,
	} = useChatViewEntry(initialContextView);
	const panel = useContactLeadPanelSection(onSectionOpen, leadCtxView);
	const [showProspectImage, setShowProspectImage] = useState(true);
	const [companyLogoError, setCompanyLogoError] = useState(false);
	// Once the rep scrolls the identity block (person + company + placement) mostly out of view, the
	// full block is replaced by a slim sticky summary strip and the tabs row sticks below it — see
	// the v81 mockup's `.lp-strip`.
	const { condensed, setCondensed, scrollRef, identityRef, anchorRef, onScroll } =
		useCondenseOnScroll();

	// The Chat view and the reach-out action row both need the lead's most-recent `ListPerson`
	// (prospect conversations, copywriting output, campaign state). Hosts that already know it pass
	// `chatListPersonId` (Global Search); otherwise resolve it from the detail's list memberships so
	// every surface (Contacts, extension) can reach the Chat view and drive the action row too.
	const resolvedListPersonId =
		composedListPersonId ?? chatListPersonId ?? resolveChatListPersonId(leadPanelDetail);
	const { data: chatLead } = useGetSingleListPersonQuery(resolvedListPersonId ?? 0, {
		skip: !resolvedListPersonId,
	});
	const showChat = !!resolvedListPersonId && !!chatLead;
	// Same tab set as the other lead panels (list DetailView / Profile Tracker): Research is
	// feature-gated, and Profiles lists the lead's colleagues — keyed on the resolved ListPerson,
	// like the Chat tab (without a list membership the tab shows its empty state).
	const isDeepResearchActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);
	const { data: organizationProfiles, isLoading: isProfilesLoading } =
		useGetOrganizationProfilesQuery(resolvedListPersonId ?? 0, {
			skip: !resolvedListPersonId,
		});
	// A Chat-enabled host (chatPending) opens the panel before the Chat tab's data is ready: first the
	// lead detail loads (which yields the chatListPersonId), then the ListPerson the Chat tab needs.
	// Show a spinner over the body until that resolves, so the Lead Panel | Chat tabs render together
	// rather than the toggle popping in after the body. Key the spinner off *data presence* rather than
	// the transient `isLeadPanelDetailLoading` flag: on the first render after open the detail is still
	// `null` but the flag may not have flipped to `true` yet (it's set `false` in `openContactLeadPanel`,
	// and a cache-served detail never fires `matchPending` at all), which would otherwise flash the
	// preview body before the spinner.
	const detailReady = !!leadPanelDetail || !!leadPanelDetailError;
	// Nothing more to wait for once the detail says the lead is on no list (no Chat tab), or the Chat
	// tab's ListPerson has loaded.
	const chatReady = !resolvedListPersonId || !!chatLead;
	// The Compose-list add has returned a `ListPerson` but its full row is still loading — hold the
	// same spinner so the lead-panel body doesn't flash before the Chat view takes over.
	const isLoadingComposedChat = !!composedListPersonId && !chatLead;
	const isResolvingChat = isLoadingComposedChat || (chatPending && (!detailReady || !chatReady));

	useEffect(() => {
		panel.setActiveTab(initialTab);
		// The host's intent for the fresh lead (P6 D6): "chat" opens straight on the
		// drafts; the prop is also a dependency so a same-lead intent change (e.g.
		// "Write manually" on the already-selected feed card) still applies.
		resetView(initialContextView);
		setShowProspectImage(true);
		setCompanyLogoError(false);
		setCondensed(false);
		setComposedListPersonId(null);
		// Reset the scroll position so a freshly opened lead starts at the top with the full identity
		// block showing.
		scrollRef.current?.scrollTo?.({ top: 0 });
	}, [leadPanelProspect?.id, initialContextView, initialTab, setCondensed, scrollRef, resetView]);

	useEffect(() => {
		if (!isLeadPanelOpen || !closeOnOutsideClick) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			// MUI modals/menus opened from the Chat tab (the language auto-detect and framework choosers,
			// both built on the shared MUI `Modal`) portal to `document.body` — *outside* `panelEl`. A
			// click inside one would otherwise read as "outside the panel" and close it, unmounting the
			// modal with it. Ignore clicks landing in any MUI overlay root. Intentional, stable coupling:
			// the shared `Modal` is MUI-based, so these portal markers won't change under us.
			const target = event.target as HTMLElement | null;

			if (target?.closest?.(".MuiModal-root, .MuiPopover-root, .MuiPopper-root")) {
				return;
			}

			if (event.target && panelEl && !composedPathContains(panelEl, event)) {
				dispatch(closeContactLeadPanel());
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [dispatch, isLeadPanelOpen, closeOnOutsideClick, panelEl]);

	if (!isLeadPanelOpen || !leadPanelProspect) {
		return null;
	}

	const prospectCv = sortEmploymentsMostRecentFirst(leadPanelDetail?.prospectEmployments || []);
	const currentEmploymentDateRange = formatCurrentEmploymentDateRange(prospectCv);

	const currentCompanyUrl = resolveCurrentCompanyUrl(leadPanelProspect.organization);

	const prospect = leadPanelProspect as unknown as People;
	const organization = leadPanelProspect.organization as unknown as Organization | undefined;

	// The action row reads list/tracker/CRM membership from the prospect's join arrays; the preview
	// carries them reliably (mirrors the `lists` fallback above), with the detail as a backstop.
	const firstName = leadPanelProspect.firstName || (leadPanelProspect.name?.split(" ")[0] ?? "");
	const membershipListPersons =
		leadPanelProspectPreview?.listPersons ?? leadPanelDetail?.listPersons;
	const membershipTrackerPersons =
		leadPanelProspectPreview?.trackerPersons ?? leadPanelDetail?.trackerPersons;
	const membershipCrmProspects =
		leadPanelProspectPreview?.crmProspects ?? leadPanelDetail?.crmProspects;

	const researchResponse = leadPanelDetail?.deepResearchResponse?.response;
	const researchCount = researchResponse ? Object.keys(researchResponse).length : 0;
	const profilesCount = organizationProfiles?.profiles?.length ?? 0;

	const tabs = buildContactLeadPanelTabs({
		t,
		isDeepResearchActive,
		researchCount,
		profilesCount,
	});

	const content = (
		<aside
			ref={setPanelEl}
			// `pointer-events-auto` is a no-op on the normal `document.body` portal, but lets a
			// host that portals the panel into a `pointer-events-none` overlay (the onboarding
			// tour's Deep Research step) keep the panel interactive while clicks pass through
			// around it to the tour underneath.
			className={`pointer-events-auto fixed right-0 top-0 ${isZHigher ? `z-[1500]` : `z-[150]`} flex h-full w-[450px] flex-col bg-white shadow-lg `}
			data-testid="contact-lead-panel"
			style={
				topOffsetPx ? { top: topOffsetPx, height: `calc(100% - ${topOffsetPx}px)` } : undefined
			}
		>
			{showCloseButton && (
				<CollapseGrabber
					ariaLabel={t("contacts.leadPanel.close")}
					collapsed={false}
					edge="left"
					onToggle={onCollapse ?? (() => dispatch(closeContactLeadPanel()))}
				/>
			)}

			{!isResolvingChat && showChat && (
				<LeadContextTabs view={leadCtxView} onChange={setLeadCtxView} />
			)}

			{isResolvingChat ? (
				<div
					className="flex min-h-0 flex-1 items-center justify-center"
					data-testid="lead-panel-loading"
				>
					<Loader />
				</div>
			) : showChat && leadCtxView === "chat" && chatLead ? (
				<div className="min-h-0 flex-1">
					<LeadChatTab
						autoOpenGenerateBar={autoOpenGenerateBar}
						lead={chatLead}
						portalContainer={portalContainer ?? panelEl}
						// On the web the portal target is the positioned `<aside>` itself, so the
						// "Tell AI" overlay scopes to the panel. The extension supplies its own
						// `portalContainer` (a 0×0 shadow-root host) — keep the viewport overlay there.
						scopeGeneratePanelToPortal={!portalContainer}
						onSent={() => setLeadCtxView("lead-panel")}
					/>
				</div>
			) : (
				<div
					ref={scrollRef}
					className="min-h-0 flex-1 overflow-y-auto scrollbar-hide"
					data-testid="contact-lead-panel-scroll"
					onScroll={onScroll}
				>
					<LeadPanelCondensedHeader
						companyName={organization?.name}
						email={leadPanelProspect.email}
						emailStatus={leadPanelProspect.emailStatus}
						image={leadPanelProspect.image}
						linkedinUrl={leadPanelProspect.linkedinUrl}
						name={leadPanelProspect.name}
						overallReasoning={leadPanelProspect.scorePersonaCategoryResult?.overallReasoning}
						overallScore={leadPanelProspect.scorePersonaCategoryResult?.overallScore}
						scoring={leadPanelProspect.scorePersonaCategoryResult}
						phone={leadPanelProspect.phone}
						title={leadPanelProspect.title}
						visible={condensed}
					/>

					{/* `isolate` traps the Tooltip wrappers' z-100 (persona/category pills, CRM icons)
					    inside this block so they can't paint over the sticky condensed strip (z-20). */}
					<div ref={identityRef} className={classNames("relative isolate", showChat ? "pt-3" : "")}>
						{headerMenu && (
							<div
								className="absolute right-2 top-2 z-10"
								data-testid="contact-lead-panel-header-menu"
							>
								{headerMenu}
							</div>
						)}
						<ListPersonHeader
							currentCompanyUrl={currentCompanyUrl}
							nameRowRef={anchorRef}
							currentEmploymentDateRange={currentEmploymentDateRange}
							domain="contact"
							employments={prospectCv}
							handleCopyClick={handleCopyClick}
							organization={organization}
							prospect={prospect}
							reserveHeaderMenuSpace={!!headerMenu}
							setShowProspectImage={setShowProspectImage}
							showProspectImage={showProspectImage}
						/>

						<LeadPanelActions
							crmProspects={membershipCrmProspects}
							firstName={firstName}
							listPerson={chatLead ?? null}
							listPersons={membershipListPersons}
							portalContainer={portalContainer ?? panelEl}
							prospectCompanyName={organization?.name}
							prospectId={leadPanelProspect.id}
							prospectLinkedinUrl={leadPanelProspect.linkedinUrl}
							prospectName={leadPanelProspect.name ?? ""}
							trackerPersons={membershipTrackerPersons}
							webAppUrl={webAppUrl}
							onComposeListPersonCreated={setComposedListPersonId}
							onReachOut={() => openChatView(false)}
						/>

						<CompanyHeader
							companyLogoError={companyLogoError}
							organization={organization}
							setCompanyLogoError={setCompanyLogoError}
							trackerOrganizationName={organization?.name}
							onFindMoreLeads={onFindMoreLeads}
						/>
					</div>

					<LeadPanelTabs
						activeTab={panel.activeTab}
						stickyTopPx={condensed ? LEAD_PANEL_STRIP_HEIGHT : 0}
						tabs={tabs}
						onChange={panel.selectSection}
					/>

					<div className="isolate px-4 pb-5 pt-3.5">
						{isLeadPanelDetailLoading && (
							<div
								className="mb-4 flex items-center gap-2 rounded-lg border border-border-light bg-white p-3 text-sm text-text-meta"
								data-testid="contact-lead-panel-loading"
							>
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-border-light border-l-blue-action" />
								<span>{t("contacts.leadPanel.loading")}</span>
							</div>
						)}
						{leadPanelDetailError && !isLeadPanelDetailLoading && (
							<div className="mb-4 rounded-lg border border-border-light bg-white p-3 text-sm text-text-meta">
								{t(leadPanelDetailError)}
							</div>
						)}
						{panel.activeTab === "overview" && (
							<ContactLeadOverviewTab
								recentBox={
									<RecentSignalsBox
										companyName={organization?.name}
										conversations={chatLead?.prospect?.conversations}
										firstName={firstName}
										isTracked={(membershipTrackerPersons?.length ?? 0) > 0}
										portalContainer={portalContainer ?? panelEl}
										prospectId={leadPanelProspect.id}
										prospectName={leadPanelProspect.name ?? ""}
										onSeeTimeline={() => panel.selectSection("timeline")}
									/>
								}
								belowSummary={
									// "Also on these lists" renders directly below the Summary (not above it).
									<ContactLeadLocation
										isDetailLoading={isLeadPanelDetailLoading}
										lists={leadPanelProspectPreview?.lists ?? leadPanelDetail?.lists}
										listPersons={
											leadPanelProspectPreview?.listPersons ?? leadPanelDetail?.listPersons
										}
										prospectName={leadPanelProspect.name}
										webAppUrl={webAppUrl}
									/>
								}
								organization={organization}
								prospect={prospect}
								sourceContexts={leadPanelDetail?.sourceContexts ?? []}
							/>
						)}
						{panel.activeTab === "research" && isDeepResearchActive && (
							<DeepResearchPanel
								deepResearchResponse={leadPanelDetail?.deepResearchResponse}
								// The source popovers portal here so they stay inside the styled
								// shadow scope when the extension reuses this panel.
								portalContainer={portalContainer ?? panelEl}
								prospectId={leadPanelProspect.id}
								// Only when this lead actually has a Chat view to jump to.
								onRequestSequence={showChat ? () => openChatView(true) : undefined}
							/>
						)}
						{panel.activeTab === "timeline" && <SignalTimeline prospectId={leadPanelProspect.id} />}
						{panel.activeTab === "profiles" && (
							<ListProfilesTab
								data={organizationProfiles}
								isLoading={isProfilesLoading}
								listId={chatLead?.list?.id}
								listName={chatLead?.list?.name}
								organization={organization}
								onSelectProfile={(profile) =>
									retargetToProfile(profile, leadPanelProspect.organization)
								}
							/>
						)}
					</div>
				</div>
			)}
			{renderSnackbar()}
		</aside>
	);

	return ReactDOM.createPortal(content, portalContainer ?? document.body);
};
